/**
 * `?hls_debug=1` hook for the vertical clips player (NEX-9360, docs/nex-9360-clip-hls-player-hardening.md).
 *
 * Exposes the current slide's hls.js instance and level/buffer/stall stats on `window.__clipHls`,
 * plus a bandwidth cap, so an agent or Playwright can force and observe ABR switches.
 *
 * Inert unless the gate is open: every export is a no-op, returns null or hands back what it was
 * given. Every export opens the gate itself, so no call order is required. Like clipHls.js it never
 * imports `hls.js`; the player passes the instance and loader in.
 */

import { usePage } from '@inertiajs/vue3'

const EVENT_LIMIT = 100

// hls.js event names, used when the instance does not expose its own `Hls.Events` map.
const HLS_EVENTS = {
    LEVEL_SWITCHING: 'hlsLevelSwitching',
    LEVEL_SWITCHED: 'hlsLevelSwitched',
    FRAG_LOADED: 'hlsFragLoaded',
    ERROR: 'hlsError',
}

const EMPTY_WATCHDOG_STATS = Object.freeze({ stalls: 0, nudges: 0, fallbacks: 0 })

// null until the first export evaluates it; cached for the rest of the page load after that.
let gateOpen = null

// Page-wide, so a cap set on one slide still holds after a swipe.
let bandwidthCap = null

// Page-wide too: a swipe re-registers, but the history that explains a switch survives it.
const events = []

// The registration that currently owns window.__clipHls.
let owner = null

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now())

function searchParam(name) {
    try {
        return new URLSearchParams(window.location.search).get(name)
    } catch {
        // No window (SSR) or an embedded browser that throws: treat as absent.
        return null
    }
}

/** A finite bps > 0, or null for anything else. */
function normaliseCap(bps) {
    const value = Number(bps)

    return bps !== null && bps !== '' && Number.isFinite(value) && value > 0 ? value : null
}

/** The shared Inertia props, or undefined before the Inertia app has a page (or outside one). */
function pageProps() {
    try {
        return usePage().props ?? undefined
    } catch {
        return undefined
    }
}

/**
 * Evaluates the gate on first use and caches it, whichever export gets here first. Without an
 * explicit `isProduction` it reads `props.app.isProduction` from the Inertia page, so the order
 * of calls cannot cache a wrong answer.
 */
function gate(isProduction) {
    if (gateOpen !== null) {
        return gateOpen
    }

    let settled = true

    if (isProduction === undefined) {
        const props = pageProps()

        // No page yet: answer closed for now, but leave the gate to be evaluated once there is one.
        settled = props !== undefined
        isProduction = props?.app?.isProduction
    }

    // Fail closed: only an explicit false opens it; a missing prop counts as production.
    const open = isProduction === false && searchParam('hls_debug') === '1'

    if (settled) {
        gateOpen = open

        if (open) {
            bandwidthCap = normaliseCap(searchParam('hls_cap'))
        }
    }

    return open
}

/**
 * true only with `?hls_debug=1` on a non-production app (`isProduction === false`; undefined
 * counts as production). Evaluated once per page load; later calls return the first answer, so a
 * client-side visit cannot open or close it mid-session.
 */
export function isHlsDebugEnabled({ isProduction } = {}) {
    return gate(isProduction)
}

/** `?hls_cap=<bps>` as a number, or null when absent, invalid or the gate is closed. */
export function initialBandwidthCap() {
    return gate() ? normaliseCap(searchParam('hls_cap')) : null
}

/**
 * `?hls_codec=h264|h265` → the codec the player should use regardless of HEVC support, or null
 * for any other value, when absent, or when the gate is closed (real users never get an override).
 */
export function forcedCodec() {
    if (!gate()) {
        return null
    }

    const codec = searchParam('hls_codec')

    return codec === 'h264' || codec === 'h265' ? codec : null
}

/** Appends `{ t, type, detail }` to the ring buffer. No-op when the gate is closed. */
export function recordHlsDebugEvent(type, detail = null) {
    if (!gate()) {
        return
    }

    events.push({ t: Math.round(now()), type, detail })

    if (events.length > EVENT_LIMIT) {
        events.splice(0, events.length - EVENT_LIMIT)
    }
}

function setBandwidthCap(bps) {
    bandwidthCap = normaliseCap(bps)
    recordHlsDebugEvent('bandwidth_cap', { bps: bandwidthCap })

    return bandwidthCap
}

const resolve = (value) => (typeof value === 'function' ? value() : value)

/** Seconds of the `buffered` range containing `time` that lie ahead of it; 0 when none does. */
function bufferedAheadOf(video, time) {
    const ranges = video?.buffered

    for (let i = 0; i < (ranges?.length ?? 0); i++) {
        if (time >= ranges.start(i) && time <= ranges.end(i)) {
            return ranges.end(i) - time
        }
    }

    return 0
}

function bufferedRanges(video) {
    const ranges = video?.buffered
    const out = []

    for (let i = 0; i < (ranges?.length ?? 0); i++) {
        out.push([ranges.start(i), ranges.end(i)])
    }

    return out
}

function snapshot({ hls, video, path, codec, watchdog }) {
    const level = hls ? hls.currentLevel : -1
    const info = hls?.levels?.[level]
    const currentTime = video?.currentTime ?? 0

    return {
        path: resolve(path),
        codec: resolve(codec),
        level,
        height: info?.height ?? null,
        bitrate: info?.bitrate ?? null,
        autoLevelCapping: hls ? hls.autoLevelCapping : null,
        nextAutoLevel: hls ? hls.nextAutoLevel : null,
        bandwidthEstimate: hls ? hls.bandwidthEstimate : null,
        bandwidthCap,
        currentTime,
        readyState: video?.readyState ?? 0,
        paused: video?.paused ?? true,
        bufferedAhead: bufferedAheadOf(video, currentTime),
        buffered: bufferedRanges(video),
        watchdog: watchdog?.stats?.() ?? { ...EMPTY_WATCHDOG_STATS },
    }
}

/** Subscribes the ring buffer to the instance's switch/load/error events; returns the detacher. */
function listenTo(hls) {
    if (typeof hls?.on !== 'function') {
        return () => {}
    }

    const names = { ...HLS_EVENTS, ...(hls.constructor?.Events ?? {}) }

    const handlers = [
        [names.LEVEL_SWITCHING, (_, data) => recordHlsDebugEvent('LEVEL_SWITCHING', {
            level: data?.level,
            height: data?.height,
            bitrate: data?.bitrate,
        })],
        [names.LEVEL_SWITCHED, (_, data) => recordHlsDebugEvent('LEVEL_SWITCHED', {
            level: data?.level,
            height: hls.levels?.[data?.level]?.height ?? null,
        })],
        [names.FRAG_LOADED, (_, data) => {
            const stats = data?.frag?.stats

            recordHlsDebugEvent('FRAG_LOADED', {
                sn: data?.frag?.sn,
                level: data?.frag?.level,
                bytes: stats?.loaded ?? data?.payload?.byteLength ?? null,
                ms: stats ? Math.round(stats.loading.end - stats.loading.start) : null,
            })
        }],
        [names.ERROR, (_, data) => recordHlsDebugEvent('ERROR', {
            type: data?.type,
            details: data?.details,
            fatal: data?.fatal === true,
        })],
    ]

    handlers.forEach(([event, handler]) => hls.on(event, handler))

    return () => handlers.forEach(([event, handler]) => hls.off?.(event, handler))
}

/**
 * Makes this slide the owner of `window.__clipHls`, replacing any earlier registration, and
 * returns `unregister()`. Unregistering clears the global only while this slide still owns it,
 * so a slide that lost ownership to a swipe cannot wipe its successor. No-op when closed.
 */
export function registerHlsDebug({ slug, hls = null, video = null, path, codec, watchdog = null }) {
    if (!gate() || typeof window === 'undefined') {
        return () => {}
    }

    // The previous owner's instance may live on as a prefetching slide; stop it feeding the buffer.
    owner?.detach()

    const registration = { detach: listenTo(hls) }
    owner = registration

    window.__clipHls = {
        slug,
        hls,
        video,
        stats: () => snapshot({ hls, video, path, codec, watchdog }),
        setBandwidthCap,
        events,
    }

    recordHlsDebugEvent('register', { slug, path: resolve(path), codec: resolve(codec) })

    return () => {
        registration.detach()
        registration.detach = () => {}

        if (owner === registration) {
            owner = null
            delete window.__clipHls
        }
    }
}

/**
 * Loader class for hls.js's `fLoader`: `BaseLoader` (pass `Hls.DefaultConfig.loader`) with a
 * page-wide bandwidth cap. Under a cap, success is held until `bytes * 8 / cap` seconds after
 * the first byte, and `stats.loading.end` is moved to that moment, so the ABR estimator (which
 * samples parsing.end/loading.end minus loading.start) measures the capped rate rather than the
 * real one. Returns `BaseLoader` untouched when the gate is closed.
 *
 * A debugging aid only: bytes still arrive at full speed, so it does not reproduce starvation.
 * Use CDP throttling for that.
 */
export function createCappedLoader(BaseLoader) {
    if (!gate() || typeof BaseLoader !== 'function') {
        return BaseLoader
    }

    return class CappedClipLoader extends BaseLoader {
        load(context, config, callbacks) {
            const onSuccess = callbacks.onSuccess

            super.load(context, config, {
                ...callbacks,
                onSuccess: (response, stats, ctx, networkDetails) => {
                    const cap = bandwidthCap
                    const bytes = stats?.loaded || response?.data?.byteLength || 0

                    if (!cap || !bytes) {
                        onSuccess(response, stats, ctx, networkDetails)

                        return
                    }

                    const first = stats.loading.first || stats.loading.start
                    const cappedEnd = first + (bytes * 8 * 1000) / cap
                    const finish = () => {
                        this.capTimer = null

                        // Aborted or destroyed while held: hls.js has already moved on.
                        if (!this.stats || this.stats.aborted) {
                            return
                        }

                        stats.loading.end = Math.max(stats.loading.end, cappedEnd)
                        stats.bwEstimate = (bytes * 8000) / Math.max(1, stats.loading.end - first)
                        onSuccess(response, stats, ctx, networkDetails)
                    }
                    const wait = cappedEnd - now()

                    if (wait <= 0) {
                        finish()

                        return
                    }

                    this.capTimer = setTimeout(finish, wait)
                },
            })
        }

        abort() {
            this.clearCapTimer()
            super.abort()
        }

        destroy() {
            this.clearCapTimer()
            super.destroy()
        }

        clearCapTimer() {
            if (this.capTimer) {
                clearTimeout(this.capTimer)
                this.capTimer = null
            }
        }
    }
}
